<template>
  <div class="mx-auto max-w-[1536px] space-y-8 px-3 py-4">
    <div class="flex flex-col gap-6 2xl:grid 2xl:grid-cols-10 2xl:gap-5">
      <div class="col-span-2 flex flex-col gap-6 2xl:gap-5">
        <ProfilePersonalInfo
          :name="[member.firstName, member.lastName].join(' ')"
          :avatar="member.avatarUrl ?? undefined"
          :city="member.city"
          :state="member.state"
          :country="member.country"
          :impact-points="metrics.impact"
        />

        <div class="flex flex-wrap items-center gap-2.5">
          <div
            class="rounded bg-navy p-1.5 font-Kontesa text-[15px] font-bold leading-[18px] text-white"
          >
            #{{ member.ranks.city }} {{ member.city }}
          </div>
          <div
            class="rounded bg-navy p-1.5 font-Kontesa text-[15px] font-bold leading-[18px] text-white"
          >
            #{{ member.ranks.country }} {{ member.country }}
          </div>
        </div>

        <ProfileShirtBadges
          v-if="shirts && shirts.length > 0"
          :shirts="shirts"
        />
      </div>
      <div class="col-span-5 flex flex-col gap-6 2xl:gap-5">
        <ProfileImpactStatList
          class="2xl:flex-1"
          :metrics="metrics"
          hide-actions
        />

        <ProfileImpactFamily
          :member-id="member.id"
          :size="metrics.family"
          hide-help
        />
      </div>
      <div class="col-span-3">
        <ProfileLeaderboard
          class="h-full"
          :type="member.type"
          :leaderboards="leaderboards"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { memberQueries } from "~/domains/member/queries";
import { Campaign } from "~/lib/campaigns";

definePageMeta({
  validate: async (route) => {
    if (typeof route.params.id !== "string") {
      return false;
    }

    const id = route.params.id.split("-").pop();
    if (!id) {
      return false;
    }

    return /^\d+$/.test(id);
  },
  layout: "public-profile",
  middleware: async (to) => {
    const id = Number((to.params.id as string).split("-").pop());
    const queryClient = useQueryClient();
    try {
      const member = await queryClient.fetchQuery(memberQueries.asGuest(id));
      if (member.type === "partner") {
        return navigateTo(`/partners/${member.partner!.slug}`);
      }
    } catch {
      return createError({ statusCode: 404, message: "Member not found" });
    }
  },
});

const route = useRoute();
const id = Number((route.params.id as string).split("-").pop());

const { data: member, suspense: memberSuspense } = useQuery(
  memberQueries.asGuest(id),
);

const { data: shirts, suspense: shirtsSuspense } = useQuery(
  memberQueries.shirts(id),
);

await Promise.all([memberSuspense(), shirtsSuspense()]);

if (!member.value) {
  throw createError({ statusCode: 404, message: "Member not found" });
}

const fullName = computed(() =>
  [member.value.firstName, member.value.lastName].filter(Boolean).join(" "),
);

const meta = {
  title: () => `${fullName.value}`,
  description: () => `${fullName.value}'s profile`,
};

useSeoMeta({
  ...meta,
  ogTitle: meta.title,
  ogDescription: meta.description,
});

const metrics = computed(() =>
  member.value.metrics.reduce(
    (acc, item) => {
      switch (item.campaignId) {
        case Campaign.TFTF:
          acc.trees += item.points;
          return acc;
        case Campaign.OVI:
        case Campaign.PF:
        case Campaign.CLEANHUB:
          acc.ocean += item.points;
          return acc;
        case Campaign.SARTHAK:
          acc.education += item.points;
          return acc;
        case Campaign.REDWOOD:
          return acc;
        case Campaign.REEFGEN:
          acc.seagrass += item.points;
          return acc;
      }
    },
    {
      ocean: 0,
      education: 0,
      trees: 0,
      seagrass: 0,
      family: member.value.referrals,
      impact: member.value.points,
    },
  ),
);

function mapLeaderboard(
  leaderboard: {
    id: number;
    rank: number;
    referrals: number;
    firstName: string;
    lastName: string;
    points: number;
  }[],
  memberId: number,
) {
  return leaderboard.map((item) => ({
    id: item.id,
    rank: item.rank,
    family: item.referrals,
    name: [item.firstName, item.lastName].join(" "),
    points: item.points,
    highlighted: item.id === memberId,
  }));
}

const leaderboards = computed(() => ({
  city: mapLeaderboard(member.value.leaderboards.city, member.value.id),
  state: mapLeaderboard(member.value.leaderboards.state, member.value.id),
  country: mapLeaderboard(member.value.leaderboards.country, member.value.id),
  global: mapLeaderboard(member.value.leaderboards.global, member.value.id),
}));

onMounted(() => {
  // Scroll to top
  window.scrollTo(0, 0);
});
</script>
